import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export interface SearchableSelectOption {
  value: string;
  primaryLabel: string;
  secondaryLabel?: string;
}

interface SearchableSelectProps {
  id: string;
  options: readonly SearchableSelectOption[];
  status: 'loading' | 'ready' | 'error';
  value: string;
  onValueChange: (value: string) => void;
  onRetry: () => void;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  retryLabel: string;
  searchPlaceholder: string;
  /** Shown in the trigger while no value is chosen. */
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
}

/**
 * SL-0056 (unit 3a), DEC-05/DEC-09: a single-value searchable select over a catalog read, built
 * entirely on the existing `./select` wrappers — never importing `@radix-ui/react-select` directly
 * (Resolution 3), so free typing coexists with Radix's own keyboard handling without a new
 * dependency. i18n-agnostic (Resolution 2): every user-visible string arrives as a prop, mirroring
 * `DataTable`'s own `emptyMessage`-as-prop precedent, so this component never calls
 * `useTranslation` itself.
 *
 * DEC-09 divergence (Resolution 4, `RCPT-SL3A-DR004`): `filterSearchableSelectOptions` and the
 * option-rendering JSX below are colocated with this shell component in the same file, rather than
 * exported as their own hook independent of the shell, as DEC-09's letter requires.
 * `filterSearchableSelectOptions` itself remains an exported pure function with no dependency on
 * the shell, so a later multi-select unit can still import and reuse it from here.
 */
export function filterSearchableSelectOptions(
  options: readonly SearchableSelectOption[],
  query: string,
): SearchableSelectOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return [...options];
  }
  return options.filter((option) =>
    `${option.primaryLabel}${option.secondaryLabel ?? ''}`.toLowerCase().includes(needle),
  );
}

// Resolution 3: the filter input stops every keydown from reaching Radix's own `SelectContent`
// keydown handler EXCEPT this set — `ArrowDown`/`ArrowUp` (item navigation), `Enter` (select the
// highlighted item), `Escape` (close — the DR-03 focus-containment case), and `Tab`. `Tab`'s own
// reasoning was corrected during SL-0056 execution (`RCPT-EXEC-SL0056-TAB`): Radix's own
// `SelectContent` handler DOES call `preventDefault()` on `Tab`, but focus containment itself does
// NOT depend on that call firing — `@radix-ui/react-select` composes `FocusScope` onto the same
// content node with `trapped: true`, and `@radix-ui/react-focus-scope`'s own document-level
// `focusin` listener snaps focus back into the popover whenever it lands outside, independent of
// any `preventDefault()`. What forwarding `Tab` actually buys is the ABSENCE of that round trip —
// without it, focus would leave and get yanked back, firing spurious focus events a screen reader
// announces (and potentially a native scroll). That is a real accessibility refinement, not the
// containment guarantee itself, and DEC-05's keyboard-operable claim still favours keeping it.
// `Home`/`End` are deliberately NOT in this set (Human-settled, `RCPT-CR-HOMEEND`): they keep their
// conventional text-cursor meaning inside this filter input rather than jumping to Radix's first/
// last item, even though Radix's own handler treats them identically to the arrow keys.
const KEYS_FORWARDED_TO_RADIX = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab']);

export function SearchableSelect({
  id,
  options,
  status,
  value,
  onValueChange,
  onRetry,
  loadingLabel,
  emptyLabel,
  errorLabel,
  retryLabel,
  searchPlaceholder,
  placeholder,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [query, setQuery] = React.useState('');
  const filteredOptions = filterSearchableSelectOptions(options, query);

  return (
    <div className="flex items-center gap-2">
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={status !== 'ready'}
        // Diagnosed during SL-0056 Activity 2's own integration tests (a form's `reset(...)`
        // re-selecting an already-chosen option): the filter query is this component's own
        // local state, so it otherwise survives the popover closing and reopening on the SAME
        // mounted instance — a stale query then gets typed INTO again on the next open, filtering
        // against a query that no longer matches anything. Clearing it on close is the fix.
        onOpenChange={(open) => {
          if (!open) {
            setQuery('');
          }
        }}
      >
        <SelectTrigger id={id} aria-describedby={ariaDescribedBy} aria-label={ariaLabel}>
          {status === 'loading' && loadingLabel}
          {status === 'error' && errorLabel}
          {status === 'ready' && <SelectValue placeholder={placeholder} />}
        </SelectTrigger>
        <SelectContent>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (!KEYS_FORWARDED_TO_RADIX.has(event.key)) {
                event.stopPropagation();
              }
            }}
            placeholder={searchPlaceholder}
            className="mb-1"
          />
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyLabel}</div>
          ) : (
            filteredOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span className="flex flex-col">
                  <span>{option.primaryLabel}</span>
                  {option.secondaryLabel !== undefined && (
                    <span className="text-xs text-muted-foreground">{option.secondaryLabel}</span>
                  )}
                </span>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {status === 'error' && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// #958 F: the server-search combobox (boards docs/mockups/suppliers 1.1, 1.9, 1.10, 1.13).
//
// The select above filters a list it already holds and closes on choice; the supplier picker needs
// what it cannot do: the list comes from the server as the person types, in named groups, with
// action rows after the options («Crea il fornitore «…»»), a «Cerco…» line within 200 ms, and a
// way to show the list inline on a full phone page. So it is a text input with
// `role="combobox"` and `aria-activedescendant` (WCAG 2.2 AA, the ARIA 1.2 combobox pattern): the
// focus never leaves the input, the arrows move the active row, Enter picks it, Escape closes.
// Still i18n-agnostic: every string arrives as a prop.

export interface ComboboxItem {
  readonly id: string;
  /** The row's main line, e.g. the name with the matched part in `<mark>` (`highlightMatch`). */
  readonly primary: React.ReactNode;
  readonly secondary?: React.ReactNode;
  /** An icon before the text. */
  readonly leading?: React.ReactNode;
  /** Badges and counts after the text. */
  readonly trailing?: React.ReactNode;
}

export interface ComboboxGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly ComboboxItem[];
}

/** A row after the options that does something rather than choose one («Crea…»). */
export type ComboboxAction = ComboboxItem;

export type ComboboxStatus = 'idle' | 'searching' | 'ready' | 'error';

export interface SearchComboboxProps {
  readonly id: string;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly groups: readonly ComboboxGroup[];
  readonly actions?: readonly ComboboxAction[];
  readonly status: ComboboxStatus;
  readonly onSelect: (itemId: string) => void;
  readonly onAction?: (actionId: string) => void;
  /** The listbox's accessible name («Fornitori trovati»). */
  readonly listLabel: string;
  /** Shown while the server is asked («Cerco…»). */
  readonly searchingLabel: string;
  /** Shown when the answer has no option (the actions still show). */
  readonly emptyLabel?: string;
  /** Shown instead of the options when the search failed, e.g. a notice with «Riprova». */
  readonly errorSlot?: React.ReactNode;
  /** A line under the list (what the search looks at, the keys). */
  readonly footer?: React.ReactNode;
  readonly placeholder?: string;
  readonly leading?: React.ReactNode;
  /**
   * The list always shows under the input, in the flow, never as a popover: the phone's full page
   * (board 1.10), where the list sits above the keyboard.
   */
  readonly inline?: boolean;
  readonly autoFocus?: boolean;
  readonly inputClassName?: string;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-invalid'?: boolean | undefined;
  readonly 'aria-label'?: string | undefined;
  readonly onBlur?: () => void;
  readonly onFocus?: () => void;
  /** Clears the text; shows a ✕ in the input when given and the text is not empty. */
  readonly onClear?: () => void;
  readonly clearLabel?: string;
}

/**
 * Wraps the first case-insensitive occurrence of `query` in `text` in a `<mark>`; the text alone
 * when it does not occur.
 */
export function highlightMatch(text: string, query: string): React.ReactNode {
  const needle = query.trim().toLowerCase();
  if (needle === '') return text;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-transparent font-bold text-inherit underline decoration-2 underline-offset-2">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}

type Row = { readonly kind: 'item' | 'action'; readonly row: ComboboxItem };

function rowsOf(groups: readonly ComboboxGroup[], actions: readonly ComboboxAction[]): Row[] {
  return [
    ...groups.flatMap((group) => group.items.map((row) => ({ kind: 'item' as const, row }))),
    ...actions.map((row) => ({ kind: 'action' as const, row })),
  ];
}

function OptionRow({
  domId,
  row,
  active,
  action,
  onPick,
  onHover,
}: {
  readonly domId: string;
  readonly row: ComboboxItem;
  readonly active: boolean;
  readonly action: boolean;
  readonly onPick: () => void;
  readonly onHover: () => void;
}) {
  return (
    <li
      id={domId}
      role="option"
      aria-selected={active}
      // Keep the focus in the input, so its blur does not close the list before the click lands.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPick}
      onMouseMove={onHover}
      className={cn(
        'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2.5 py-1.5 text-sm',
        // S1 (#952): the active option is the selection's blue, never the action colour.
        active ? 'bg-selected text-selected-foreground' : 'text-foreground',
        action && 'border-border mt-1 border-t pt-2',
      )}
    >
      {row.leading !== undefined && (
        <span aria-hidden="true" className={cn('shrink-0', !active && 'text-muted-foreground')}>
          {row.leading}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn('truncate', action ? 'font-semibold' : 'font-medium')}>
          {row.primary}
        </span>
        {row.secondary !== undefined && (
          <span className={cn('truncate text-xs', !active && 'text-muted-foreground')}>
            {row.secondary}
          </span>
        )}
      </span>
      {row.trailing !== undefined && (
        <span className="flex shrink-0 items-center gap-2 text-xs">{row.trailing}</span>
      )}
    </li>
  );
}

function useActiveRow(rows: readonly Row[]) {
  const [active, setActive] = React.useState(-1);
  const key = rows.map((r) => r.row.id).join('|');
  // A new answer starts with its first row active: Enter takes the best match (board 1.1).
  React.useEffect(() => setActive(rows.length > 0 ? 0 : -1), [key, rows.length]);
  const move = (step: 1 | -1): void => {
    if (rows.length === 0) return;
    setActive((i) => (i + step + rows.length) % rows.length);
  };
  return { active, setActive, move };
}

export function SearchCombobox(props: SearchComboboxProps) {
  const {
    id,
    query,
    onQueryChange,
    groups,
    actions = [],
    status,
    onSelect,
    onAction,
    inline = false,
  } = props;
  const [open, setOpen] = React.useState(false);
  // A failed search shows no options, only the actions («Crea comunque», board 1.13).
  const visibleGroups = React.useMemo(
    () => (status === 'error' ? [] : groups.filter((group) => group.items.length > 0)),
    [groups, status],
  );
  const rows = React.useMemo(() => rowsOf(visibleGroups, actions), [visibleGroups, actions]);
  const { active, setActive, move } = useActiveRow(rows);
  const listId = `${id}-listbox`;
  const statusId = `${id}-status`;
  const domIdOf = (index: number): string => `${id}-option-${index}`;
  const hasContent =
    rows.length > 0 || status === 'searching' || status === 'error' || query.trim() !== '';
  const shown = inline || (open && hasContent && status !== 'idle');

  const pick = (index: number): void => {
    const target = rows[index];
    if (!target) return;
    if (!inline) setOpen(false);
    if (target.kind === 'item') onSelect(target.row.id);
    else onAction?.(target.row.id);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!shown) {
        // Reopened from the keyboard: the best match is active again.
        setOpen(true);
        setActive(rows.length > 0 ? 0 : -1);
        return;
      }
      move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && shown && active >= 0) {
      event.preventDefault();
      // While the server is asked the rows are the previous answer's: Enter would pick a stale one.
      if (status === 'searching') return;
      pick(active);
    } else if (event.key === 'Escape' && shown && !inline) {
      event.preventDefault();
      setOpen(false);
    }
  };

  let index = -1;
  const optionRow = (row: ComboboxItem, action: boolean) => {
    index += 1;
    const at = index;
    return (
      <OptionRow
        key={`${action ? 'a' : 'i'}-${row.id}`}
        domId={domIdOf(at)}
        row={row}
        active={at === active}
        action={action}
        onPick={() => pick(at)}
        onHover={() => setActive(at)}
      />
    );
  };

  return (
    <div className={cn('relative', inline && 'flex min-h-0 flex-1 flex-col gap-2')}>
      <div className="relative">
        {props.leading !== undefined && (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          >
            {props.leading}
          </span>
        )}
        <Input
          id={id}
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={shown}
          aria-controls={listId}
          aria-activedescendant={shown && active >= 0 ? domIdOf(active) : undefined}
          aria-describedby={[props['aria-describedby'], statusId].filter(Boolean).join(' ')}
          aria-invalid={props['aria-invalid'] || undefined}
          aria-label={props['aria-label']}
          autoFocus={props.autoFocus}
          placeholder={props.placeholder}
          value={query}
          className={cn(
            'min-h-11',
            props.leading !== undefined && 'pl-9',
            props.onClear && 'pr-11',
            props.inputClassName,
          )}
          onChange={(event) => {
            onQueryChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            props.onFocus?.();
          }}
          onBlur={() => {
            if (!inline) setOpen(false);
            props.onBlur?.();
          }}
          onKeyDown={onKeyDown}
        />
        {props.onClear && query !== '' && (
          <button
            type="button"
            aria-label={props.clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={props.onClear}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-0 flex size-11 -translate-y-1/2 items-center justify-center rounded-md"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
      {/* The live line: «Cerco…» and the empty answer are announced without moving the focus. */}
      <span id={statusId} role="status" aria-live="polite" className="sr-only">
        {shown && status === 'searching' ? props.searchingLabel : ''}
        {shown && status === 'ready' && rows.length === 0 && props.emptyLabel
          ? props.emptyLabel
          : ''}
      </span>
      <div
        hidden={!shown}
        className={cn(
          inline
            ? 'min-h-0 flex-1 overflow-auto'
            : 'border-border bg-popover absolute z-30 mt-1 w-full overflow-hidden rounded-lg border shadow-md',
        )}
      >
        {status === 'error' && props.errorSlot}
        <ul
          id={listId}
          role="listbox"
          aria-label={props.listLabel}
          className={cn('p-1', !inline && 'max-h-[min(24rem,60vh)] overflow-auto')}
        >
          {/* Within 200 ms of a keystroke, above the previous answer until the new one comes. */}
          {status === 'searching' && (
            <li role="presentation" className="text-muted-foreground px-2.5 py-2 text-sm">
              {props.searchingLabel}
            </li>
          )}
          {visibleGroups.map((group) => (
            <li key={group.id} role="presentation">
              <ul role="group" aria-labelledby={`${id}-group-${group.id}`}>
                <li
                  role="presentation"
                  id={`${id}-group-${group.id}`}
                  className="text-muted-foreground px-2.5 pt-2 pb-1 text-xs font-semibold"
                >
                  {group.label}
                </li>
                {group.items.map((row) => optionRow(row, false))}
              </ul>
            </li>
          ))}
          {status === 'ready' && rows.length === actions.length && props.emptyLabel && (
            <li role="presentation" className="text-muted-foreground px-2.5 py-2 text-sm">
              {props.emptyLabel}
            </li>
          )}
          {actions.map((row) => optionRow(row, true))}
        </ul>
        {props.footer !== undefined && (
          <div className="border-border text-muted-foreground flex flex-wrap justify-between gap-2 border-t px-3 py-2 text-xs">
            {props.footer}
          </div>
        )}
      </div>
    </div>
  );
}
